
/**
 * The console's chart vocabulary — three forms, per DESIGN.md §11.
 *
 * **Why these live in one file.** §11.3 says every chart declares its sample size and withholds colour below
 * the floor, and §11.2 says every chart states its figures as text as well as geometry. Those are the rules
 * most easily forgotten one page at a time — the pattern this codebase keeps repeating is a rule written down,
 * then re-implemented slightly differently at each call site until the versions disagree. So the floor and the
 * empty state are implemented **once**, here, and a page cannot draw a chart without inheriting them.
 *
 * Deliberately three components and not a generic chart factory. The three forms answer three different reader
 * questions (§11.1) and the whole point is that a page picks the form by naming the question, rather than
 * choosing a chart type and hoping it fits.
 *
 * No pie, donut, radial or treemap anywhere in here — `CLAUDE.md` rule 26 makes that greppable.
 */

/** Below this many samples a percentage swings too wildly to colour. DESIGN.md §11.3. */
export const SAMPLE_FLOOR = 5

/**
 * The shared frame: heading, the honest-empty and below-floor states, then the chart.
 *
 * Every chart in the console goes through this, so "no data" and "too few to judge" look the same everywhere
 * and cannot be forgotten. `sampleNote` is rendered even when the chart draws — §11.2's rule that the figures
 * are always available as text, not only as geometry.
 */
function ChartFrame({ title, hint, sampleSize, sampleNote, empty, children }) {
  const none = !sampleSize
  return (
    <div className="chart-block">
      <div className="chart-head">
        <strong>{title}</strong>
        {hint && <span className="muted">{hint}</span>}
      </div>
      {none
        // An em dash and a sentence, never a zeroed chart: an empty bar reads as a real measurement of zero,
        // which is the opposite of "nothing has happened yet".
        ? <p className="chart-empty">— {empty || 'No data in this period'}</p>
        : (
          <>
            {children}
            {sampleNote && <p className="chart-note muted">{sampleNote}</p>}
          </>
        )}
    </div>
  )
}

/**
 * **"What share of the whole?"** — one horizontal stacked bar, each segment labelled with its value.
 *
 * Not a donut (§11.1): a ring destroys sequence, and MTTR's four stages are consecutive — response, then
 * repair, then hold, then verification. Reading "hold dominates" off a bar is immediate; off a ring it is
 * guesswork. Segments narrower than the label are left unlabelled in the bar and carried by the legend below,
 * which is why the legend prints values too rather than only colours.
 *
 * @param segments [{ label, value, token, hint }] in the order they occur.
 * @param unit rendered after each value, e.g. "h" or "min".
 */
export function StackedShareBar({ title, hint, segments, unit = '', sampleSize, sampleNote, empty }) {
  const parts = (segments || []).filter((s) => Number(s.value) > 0)
  const total = parts.reduce((a, s) => a + Number(s.value), 0)

  return (
    <ChartFrame
      title={title}
      hint={hint}
      sampleSize={total > 0 ? (sampleSize ?? 1) : 0}
      sampleNote={sampleNote}
      empty={empty}
    >
      <div className="share-bar" role="img" aria-label={parts.map((s) => `${s.label} ${s.value}${unit}`).join(', ')}>
        {parts.map((s) => {
          const pct = (Number(s.value) / total) * 100
          return (
            <span
              key={s.label}
              className="share-seg"
              style={{ width: `${pct}%`, background: `var(--${s.token})` }}
              title={`${s.label}: ${fmt(s.value)}${unit} (${pct.toFixed(0)}%)`}
            >
              {/* Only label inside the bar when the segment can hold the text; the legend carries the rest. */}
              {pct >= 12 && <span className="share-seg-label tnum">{fmt(s.value)}{unit}</span>}
            </span>
          )
        })}
      </div>
      <ul className="share-legend">
        {parts.map((s) => (
          <li key={s.label}>
            <span className="share-swatch" style={{ background: `var(--${s.token})` }} aria-hidden="true" />
            <span>{s.label}</span>
            {/* Value and share as text — §11.2. A legend of colours alone is the failure this rule exists for. */}
            <span className="tnum">{fmt(s.value)}{unit} · {((Number(s.value) / total) * 100).toFixed(0)}%</span>
            {s.hint && <span className="muted">{s.hint}</span>}
          </li>
        ))}
      </ul>
    </ChartFrame>
  )
}

/**
 * **"How far from the target?"** — a progress bar with a target marker.
 *
 * Not a donut and not a gauge (§11.1). The reader's question is distance from a goal, and only a linear scale
 * with the target drawn on it answers that: "80% against a target of 80%" and "80% against a target of 95%"
 * are different situations that a ring renders identically.
 *
 * Colour is withheld below [SAMPLE_FLOOR] samples — one late round in three is 67%, which would paint a red
 * bar over a plant that has barely started (§11.3).
 */
export function TargetProgressBar({
  title, hint, value, target, unit = '%', sampleSize, sampleNote, empty, higherIsBetter = true,
}) {
  const has = value !== null && value !== undefined
  const judged = has && sampleSize >= SAMPLE_FLOOR
  const kind = !judged ? 'neutral' : verdict(value, target, higherIsBetter)

  return (
    <ChartFrame
      title={title}
      hint={hint}
      sampleSize={has ? (sampleSize || 1) : 0}
      sampleNote={sampleNote}
      empty={empty}
    >
      <div className="progress-row">
        <span className={`progress-value tnum${judged ? ` val-${kind}` : ''}`}>{fmt(value)}{unit}</span>
        <div className="progress-track" role="img" aria-label={`${fmt(value)}${unit} against a target of ${target}${unit}`}>
          <span
            className={`progress-fill progress-${kind}`}
            style={{ width: `${Math.max(0, Math.min(100, Number(value)))}%` }}
          />
          {/* The target is a line on the scale, not a number in a caption: the gap is the message. */}
          <span className="progress-target" style={{ left: `${Math.max(0, Math.min(100, target))}%` }} title={`Target ${target}${unit}`} />
        </div>
        <span className="muted">target {target}{unit}</span>
      </div>
    </ChartFrame>
  )
}

/**
 * **"Which is worst?"** — a ranked horizontal bar chart.
 *
 * Horizontal because the labels are words: `WIRE_ROPE`, `Neutral Interlock System`. Under a vertical bar those
 * rotate, and rotated text is unreadable at a glance (§11.1).
 *
 * Truncation is explicit. [topN] caps the bars and the note says how many were dropped — a chart that silently
 * drops its tail reads as "this is everything", which is `CLAUDE.md` rule 30.
 *
 * @param rows [{ label, value, token? }] in any order; sorted here, descending.
 * @param onSelect optional drill-down; makes the bars clickable.
 */
export function RankedBar({
  title, hint, rows, unit = '', topN = 8, sampleSize, sampleNote, empty, onSelect, height,
}) {
  // Zero-value rows are dropped, not drawn. A category with no failures still claims an axis row and a slot in
  // the top-N, so three empty labels pushed real findings out of the chart — measured on the inspection
  // findings chart, where "L/T Platform 0" sat above sections that had actual failures. "Never failed" belongs
  // in the table beside it, not as a bar of length zero.
  const all = [...(rows || [])]
    .filter((r) => Number(r.value) > 0)
    .sort((a, b) => Number(b.value) - Number(a.value))
  const shown = all.slice(0, topN)
  const dropped = all.length - shown.length
  const note = [sampleNote, dropped > 0 ? `+${dropped} more not shown` : null].filter(Boolean).join(' · ')

  return (
    <ChartFrame
      title={title}
      hint={hint}
      sampleSize={sampleSize ?? all.length}
      sampleNote={note || undefined}
      empty={empty}
    >
      {/*
        Bars in CSS, not in recharts.

        This chart went through four attempts at recharts' label machinery — `label={{ formatter }}` (rendered
        nothing), `LabelList` with `style` (wrong colour), with a `fill` prop (`var()` in an SVG presentation
        attribute is never resolved), and with a class plus `!important` (recharts still wrote an inherited
        `fill` attribute from the Cell). Each time the DOM contained the numbers at correct coordinates,
        reported `visibility: visible`, and painted nothing readable — the gap between "the element exists" and
        "a person can read it", which is why this was caught by screenshots and not by DOM queries.

        A ranked bar is two divs and a width percentage. Writing it directly costs less than the workarounds,
        makes the value label unmissable, keeps every colour a token (§11.5), and the labels are real DOM text
        so a screen reader announces them. recharts stays where it earns its keep — the trend and Pareto charts,
        which need axes and a cumulative line.
      */}
      <ul className="ranked-bars">
        {shown.map((r) => {
          const max = Math.max(...shown.map((x) => Number(x.value)))
          const pct = max > 0 ? (Number(r.value) / max) * 100 : 0
          const Tag = onSelect ? 'button' : 'div'
          return (
            <li key={r.label}>
              <Tag
                className={`ranked-row${onSelect ? ' ranked-click' : ''}`}
                onClick={onSelect ? () => onSelect(r) : undefined}
                type={onSelect ? 'button' : undefined}
                title={`${r.label}: ${fmt(r.value)}${unit}`}
              >
                <span className="ranked-label">{r.label}</span>
                <span className="ranked-track">
                  {/* Width is the share of the largest bar, so rank is visible at a glance; the number beside
                      it carries the magnitude that a relative length cannot (§11.2). */}
                  <span
                    className="ranked-fill"
                    style={{ width: `${Math.max(2, pct)}%`, background: `var(--${r.token || 'sail-blue'})` }}
                  />
                </span>
                <span className="ranked-value tnum">{fmt(r.value)}{unit}</span>
              </Tag>
            </li>
          )
        })}
      </ul>
    </ChartFrame>
  )
}

/* ---- Bits ---------------------------------------------------------------- */

/** One decimal at most, and no trailing `.0` — a plant reads "9 days", not "9.0 days". */
function fmt(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

function verdict(value, target, higherIsBetter) {
  const v = Number(value)
  const hit = higherIsBetter ? v >= target : v <= target
  if (hit) return 'success'
  // Within a tenth of the target is "close", not "failing": a 78% against an 80% target is not the same
  // situation as 40%, and colouring both red tells the reader nothing about which to act on.
  const near = higherIsBetter ? v >= target * 0.9 : v <= target * 1.1
  return near ? 'warning' : 'danger'
}
